import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getAdminSystemMappers, deleteSystemMapper, downloadSystemMapperPDF } from "@/lib/api";
import { Trash2, Loader2, Network, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminSystemMapper() {
  const [landscapes, setLandscapes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await getAdminSystemMappers(); setLandscapes(res.data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this landscape?")) return;
    try { await deleteSystemMapper(id); setLandscapes(l => l.filter(x => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const handleDownloadPDF = async (l) => {
    try {
      toast.info("Generating PDF...");
      const res = await downloadSystemMapperPDF(l.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Landscape_${l.company_name.replace(/\s+/g, '_')}_${l.id.slice(0, 8)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch { toast.error("Failed to download PDF"); }
  };

  const avgScore = landscapes.length > 0
    ? Math.round(landscapes.reduce((s, l) => s + (l.calculated_metrics?.integration_score || 0), 0) / landscapes.length)
    : 0;

  return (
    <AdminLayout title="System Landscape Mapper">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-indigo-600">{landscapes.length}</div><div className="text-xs text-slate-500">Total Submissions</div></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-amber-600">{avgScore}/100</div><div className="text-xs text-slate-500">Avg Integration Score</div></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-red-600">{landscapes.filter(l => (l.calculated_metrics?.integration_score || 0) < 30).length}</div><div className="text-xs text-slate-500">Heavily Siloed Leads</div></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : landscapes.length === 0 ? (
            <div className="p-8 text-center text-slate-400"><Network className="w-8 h-8 mx-auto mb-2" />No submissions yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Integration Score</TableHead>
                  <TableHead>Tools</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {landscapes.map(l => {
                  const score = l.calculated_metrics?.integration_score || 0;
                  return (
                    <TableRow key={l.id}>
                      <TableCell className="font-medium">{l.company_name}</TableCell>
                      <TableCell><div className="text-sm">{l.contact_name}</div><div className="text-xs text-slate-400">{l.contact_email}</div></TableCell>
                      <TableCell className="text-sm">{l.industry || "-"}</TableCell>
                      <TableCell><span className={`font-bold ${score >= 60 ? "text-green-600" : score >= 30 ? "text-amber-600" : "text-red-600"}`}>{score}/100</span></TableCell>
                      <TableCell className="text-sm">{l.calculated_metrics?.total_tools || 0} systems</TableCell>
                      <TableCell className="text-xs text-slate-500">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setSelected(l)}><Eye className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(l)} data-testid={`download-mapper-pdf-${l.id}`} title="Download PDF"><Download className="w-4 h-4 text-[#0066CC]" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(l.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selected?.company_name} — Landscape Report</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <div className="text-3xl font-bold text-indigo-600">{selected.calculated_metrics?.integration_score}/100</div>
                <div className="text-sm text-slate-600">Integration Score</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-slate-50 rounded text-sm"><span className="text-slate-500">Tools: </span><span className="font-bold">{selected.calculated_metrics?.total_tools}</span></div>
                <div className="p-2 bg-slate-50 rounded text-sm"><span className="text-slate-500">Silos: </span><span className="font-bold">{selected.calculated_metrics?.standalone_departments}</span></div>
                <div className="p-2 bg-slate-50 rounded text-sm"><span className="text-slate-500">Connections: </span><span className="font-bold">{selected.calculated_metrics?.integration_connections}</span></div>
                <div className="p-2 bg-slate-50 rounded text-sm"><span className="text-slate-500">Single Source: </span><span className="font-bold">{selected.calculated_metrics?.has_single_source ? "Yes" : "No"}</span></div>
              </div>
              {selected.auto_problems?.length > 0 && (
                <div className="p-3 bg-red-50 rounded border border-red-100">
                  <p className="text-xs font-semibold text-red-700 mb-2">Problems ({selected.auto_problems.length}):</p>
                  {selected.auto_problems.map((p, i) => (
                    <p key={`prob-${i}-${p.slice(0, 20)}`} className="text-xs text-red-800 mb-1">- {p}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
